import { requestId } from "../api/trackPayloads";
import Alert from "@mui/material/Alert";
import useEditorForm from "../hooks/useEditorForm";
import { useForm } from "react-hook-form";
import useFormField from "../hooks/useFormField";
import SearchIcon from "@mui/icons-material/Search";
import TextInput from "../ui/TextInput";
import Button from "@mui/material/Button";
import React from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { API_BASE, fetchJson } from "../utils";
import { localReturnPath } from "../utils/navigation";
import { nextSetName } from "../utils/setTracks";
import useSetCollection from "../hooks/useSetCollection";
export default function AddTrackToSet() {
  const [params] = useSearchParams();
  const trackId = params.get("track");
  const returnTo = localReturnPath(params.get("return"));
  if (!trackId)
    return (
      <Alert role="alert" severity="error">
        Choose a track before adding it to a set.
      </Alert>
    );
  return (
    <AddTrackToSetContent
      key={`${trackId}:${returnTo}`}
      trackId={trackId}
      returnTo={returnTo}
    />
  );
}
function AddTrackToSetContent({ trackId, returnTo }) {
  const form = useForm({
    defaultValues: {
      query: "",
    },
    mode: "onChange",
  });
  const { sets, setSets, loading, error, reload } = useSetCollection();
  const [mutationError, setMutationError] = React.useState(null);
  const [pending, setPending] = React.useState(false);
  const busy = React.useRef(false);
  const active = React.useRef(true);
  const [query, setQuery] = useFormField(form, "query");
  const [editing, setEditing, editorForm] = useEditorForm({
    id: null,
    name: "",
    original: "",
    addAfter: false,
  });
  const navigate = useNavigate();
  React.useEffect(() => {
    active.current = true;
    return () => {
      active.current = false;
    };
  }, []);
  const renameSet = async (id, name) => {
    try {
      await fetchJson(`${API_BASE}/user-sets/${id}/`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          name,
        }),
      });
      setSets((s) =>
        s.map((set) =>
          set.id === id
            ? {
                ...set,
                name,
              }
            : set,
        ),
      );
    } catch (err) {
      setMutationError(err);
      throw err;
    }
  };
  const finishRename = async () => {
    if (busy.current) return;
    const { id, name, original, addAfter } = editing;
    if (!id) return;
    const trimmed = name.trim();
    if (!trimmed) {
      setMutationError(new Error("Set name cannot be empty."));
      return;
    }
    busy.current = true;
    setPending(true);
    setMutationError(null);
    try {
      if (trimmed !== original) await renameSet(id, trimmed);
      setEditing({
        id: null,
        name: "",
        original: "",
        addAfter: false,
      });
      if (addAfter && active.current) await addTrack(id);
    } catch (err) {
      setMutationError(err);
    } finally {
      busy.current = false;
      setPending(false);
    }
  };
  const createSetAndAdd = async () => {
    if (!trackId || busy.current || loading) return;
    busy.current = true;
    setPending(true);
    setMutationError(null);
    const name = nextSetName(sets);
    try {
      const set = await fetchJson(`${API_BASE}/user-sets/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          name,
        }),
      });
      setSets((s) => [...s, set]);
      setEditing({
        id: set.id,
        name: set.name,
        original: set.name,
        addAfter: true,
      });
    } catch (err) {
      setMutationError(err);
    } finally {
      busy.current = false;
      setPending(false);
    }
  };
  const addTrack = async (id) => {
    await fetchJson(`${API_BASE}/user-sets/${id}/tracks/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify({
        track_id: requestId(trackId),
      }),
    });
    if (active.current) navigate(returnTo);
  };
  const handleAdd = async (id) => {
    if (!trackId || busy.current) return;
    busy.current = true;
    setPending(true);
    setMutationError(null);
    try {
      await addTrack(id);
    } catch (err) {
      setMutationError(err);
    } finally {
      busy.current = false;
      setPending(false);
    }
  };
  const filteredSets = query
    ? sets.filter((s) => s.name.toLowerCase().includes(query.toLowerCase()))
    : sets;
  if (error)
    return (
      <Alert role="alert" severity="error">
        Error loading sets: {error.message}{" "}
        <Button className="btn" onClick={reload}>
          Retry
        </Button>
      </Alert>
    );
  return (
    <div className="add-track-to-set-page">
      {loading && <p role="status">Loading sets…</p>}
      {mutationError && (
        <Alert role="alert" severity="error">
          {mutationError.message}
        </Alert>
      )}
      <div className="search-bar">
        <div className="album-search">
          <TextInput
            autoFocus
            type="text"
            aria-label="Search sets"
            placeholder="Search sets..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <span className="search-icon">
            <SearchIcon />
          </span>
        </div>
      </div>
      <ul className="album-grid">
        <li className="album-tile">
          <Button
            className="album-thumb"
            aria-label="New Set"
            disabled={pending || loading}
            onClick={createSetAndAdd}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "3rem",
            }}
          >
            +
          </Button>
          <div className="album-info">
            <div className="album-title">New Set</div>
          </div>
        </li>
        {filteredSets.map((set) => (
          <li key={set.id} className="album-tile">
            <div className="album-thumb">
              <img
                src={`${API_BASE}/user-sets/${set.id}/image/`}
                alt={`${set.name} artwork`}
                loading="lazy"
              />
            </div>
            <div className="album-info">
              {editing.id === set.id ? (
                <TextInput
                  className="set-edit-input"
                  type="text"
                  aria-label="Set name"
                  disabled={pending}
                  value={editing.name}
                  onChange={(e) =>
                    setEditing((ed) => ({
                      ...ed,
                      name: e.target.value,
                    }))
                  }
                  onBlur={editorForm.handleSubmit(finishRename)}
                  onKeyDown={(e) => e.key === "Enter" && e.target.blur()}
                  autoFocus
                />
              ) : (
                <Button
                  className="btn"
                  disabled={pending}
                  onClick={() => handleAdd(set.id)}
                >
                  Add to {set.name}
                </Button>
              )}
              <div>
                {set.track_count ?? 0} tracks
                {set.total_duration ? ` • ${set.total_duration}` : ""}
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
